/**
 * workspaceHub/styled.ts
 * Qué hace: styled-components del hub de workspace (header de marca, panel de
 *   completitud, alertas, grilla de posts recientes). CSS puro, sin lógica.
 * Quién lo usa: pages/WorkspaceHubPage.tsx.
 *
 * Layout: NO es una columna centrada. Header a lo ancho + dos columnas
 * (contenido 1.6fr / panel lateral 1fr) que colapsan a una bajo tablet.
 *
 * Pasa el cap de 500 LOC a propósito: es CSS de una sola pantalla, sin lógica.
 * Partirlo por sección obligaría a saltar entre archivos para seguir un layout
 * que se lee de arriba abajo (header → grilla → cards → posts).
 */
import styled, { keyframes, css } from 'styled-components';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* Aparición escalonada. Va SIEMPRE en un wrapper y nunca en el elemento que
   tiene :hover con transform: con fill-mode both el estado final del keyframe
   le gana a la declaración del hover y el hover deja de verse. */
const enter = (delayMs: number) => css`
  animation: ${fadeInUp} 0.34s ease-out both;
  animation-delay: ${delayMs}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const HubLayout = styled.div`
  max-width: 1680px;
  margin: 0 auto;
  padding: 0 24px 40px;
  width: 100%;
`;

/* ── Header de marca ── */
export const BrandHeader = styled.header`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: 20px 0 18px;
  flex-wrap: wrap;
  ${enter(0)}
`;

export const BrandIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex: 1 1 320px;
`;

/* Mismo criterio que las cards del dashboard: tile con padding y contain, para
   que un logo apaisado no quede recortado. */
export const BrandLogoTile = styled.div`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  padding: 10px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const BrandLogoFallback = styled.div<{ $gradient: string }>`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: ${({ $gradient }) => $gradient};
  color: #fff;
  font-size: 30px;
  font-weight: 700;
`;

export const BrandTitleBlock = styled.div`
  min-width: 0;
`;

export const BrandName = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
  text-wrap: balance;
`;

export const BrandMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
`;

export const WebsiteLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MetaDot = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: 0;
    width: 100%;
  }
`;

export const ActionButton = styled.button<{ $primary?: boolean }>`
  height: 40px;
  padding: 0 18px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease,
    border-color 0.15s ease, color 0.15s ease;

  ${({ $primary, theme }) => ($primary
    ? css`
        border: 1px solid transparent;
        background: ${theme.colors.primary};
        color: #fff;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(59, 130, 246, 0.28);

        &:hover { background: ${theme.colors.primaryHover}; transform: translateY(-1px); }
      `
    : css`
        border: 1px solid ${theme.colors.border};
        background: ${theme.colors.surface};
        color: ${theme.colors.text};
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

        &:hover { border-color: ${theme.colors.borderHover}; transform: translateY(-1px); }
      `)}

  &:active { transform: scale(0.96); }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

/* ── Grilla principal ── */
export const HubGrid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 20px;
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

export const Card = styled.section<{ $delay?: number }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.03);
  ${({ $delay }) => enter($delay ?? 60)}
`;

export const CardHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

export const CardTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
`;

export const CardLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover { text-decoration: underline; text-underline-offset: 3px; }
`;

export const Overview = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-wrap: pretty;
`;

export const EmptyHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.6;
`;

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
`;

export const Tag = styled.span`
  padding: 5px 11px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ── Completitud ── */
export const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

/* El anillo y el número se SUPERPONEN. Ojo: como hermanos dentro de un grid,
   por defecto caen en dos filas distintas y el número termina debajo del
   círculo — el anillo va absoluto y el número relativo encima. */
export const ScoreRingWrap = styled.div`
  position: relative;
  width: 68px;
  height: 68px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
`;

export const ScoreRing = styled.div<{ $percent: number; $color: string }>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  /* El anillo es un conic-gradient: sin SVG y sin dependencias. */
  background: conic-gradient(
    ${({ $color }) => $color} ${({ $percent }) => $percent * 3.6}deg,
    ${({ theme }) => theme.colors.surfaceHover} 0deg
  );

  /* El agujero del donut. */
  &::after {
    content: '';
    position: absolute;
    inset: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.surface};
  }
`;

export const ScoreValue = styled.span`
  position: relative;
  z-index: 1;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  font-variant-numeric: tabular-nums;
`;

export const ScoreText = styled.div`
  min-width: 0;

  strong {
    display: block;
    font-size: ${({ theme }) => theme.fontSize.md};
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 2px;
  }

  span {
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    line-height: 1.5;
  }
`;

export const CheckList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/* Cada fila lleva a donde se configura ESE dato. Antes era un <li> muerto: el
   checklist te decía qué falta y te dejaba buscarlo a mano. */
export const CheckRow = styled.li`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child { border-bottom: none; }
`;

export const CheckAction = styled.button<{ $done: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 6px;
  margin: 0 -6px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: left;
  cursor: pointer;
  color: ${({ theme, $done }) => ($done ? theme.colors.textMuted : theme.colors.text)};
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }

  &:active { transform: scale(0.99); }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }

  /* La flecha aparece al pasar por encima: insinúa que lleva a algún lado sin
     ensuciar las once filas con un chevron permanente. */
  &:hover .go,
  &:focus-visible .go { opacity: 1; transform: translateX(0); }
`;

export const CheckGo = styled.span`
  flex-shrink: 0;
  opacity: 0;
  transform: translateX(-3px);
  transition: opacity 0.15s ease, transform 0.15s ease;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1;
`;

export const CheckMark = styled.span<{ $done: boolean; $required: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  color: #fff;
  background: ${({ theme, $done, $required }) => {
    if ($done) return theme.colors.success;
    return $required ? theme.colors.error : theme.colors.textMuted;
  }};
`;

export const CheckLabel = styled.span`
  flex: 1;
  min-width: 0;
`;

export const RequiredBadge = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.error};
`;

/* ── Alerta de datos faltantes ── */
export const Alert = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.warning}14;
  border: 1px solid ${({ theme }) => theme.colors.warning}40;
  margin-bottom: 18px;
  ${enter(30)}
`;

export const AlertIcon = styled.span`
  color: ${({ theme }) => theme.colors.warning};
  flex-shrink: 0;
  display: flex;
  padding-top: 1px;
`;

export const AlertBody = styled.div`
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.55;

  strong { font-weight: ${({ theme }) => theme.fontWeight.semibold}; }

  p {
    margin: 4px 0 0;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

export const AlertFix = styled.button`
  margin-top: 8px;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover { text-decoration: underline; text-underline-offset: 3px; }
`;

/* ── Posts recientes ── */
export const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 12px;
`;

export const PostCell = styled.div<{ $i: number }>`
  ${({ $i }) => enter(80 + Math.min($i * 20, 200))}
`;

export const PostThumb = styled.button`
  width: 100%;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surfaceHover};
  cursor: pointer;
  display: block;
  position: relative;
  aspect-ratio: 1 / 1;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.16);
  }

  &:active { transform: scale(0.96); }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

/* Post sin imagen: en vez de un hueco gris, mostramos el texto del post. */
export const PostTextFallback = styled.span`
  position: absolute;
  inset: 0;
  padding: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: left;
`;

export const PostNetwork = styled.span`
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  text-transform: capitalize;
  backdrop-filter: blur(4px);
`;

/* ── Paleta de marca ── */
export const Swatches = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const Swatch = styled.span<{ $color: string }>`
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: ${({ $color }) => $color};
  border: 1px solid rgba(0, 0, 0, 0.12);
`;

/* ── Stats ── */
export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

export const Stat = styled.div`
  padding: 12px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.surfaceHover};
  text-align: center;

  strong {
    display: block;
    font-size: ${({ theme }) => theme.fontSize.xl};
    color: ${({ theme }) => theme.colors.text};
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }

  span {
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

export const NotFoundBox = styled.div`
  padding: 40px 24px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
